import { useState } from 'react';
import { Lock, Check, Clock, Circle, DollarSign, Calendar, Flag, ChevronDown, ChevronUp } from 'lucide-react';

const STATUS_PASSWORD = 'growth';

// Project Info from signed SOW
const PROJECT_INFO = {
  name: 'Traction Bee MVP',
  client: 'Sophie Wang (Traction Bee)',
  developer: 'David Bramante (Codex Deus)',
  effectiveDate: 'Tuesday, February 24, 2026',
  targetLaunch: 'Friday, March 27, 2026',
  totalBudget: 20000,
  sprintCost: 4000,
  totalSprints: 5,
};

type FeatureStatus = 'completed' | 'in_progress' | 'pending';

interface Feature {
  id: string;
  name: string;
  description: string;
  status: FeatureStatus;
}

interface Sprint {
  id: string;
  name: string;
  focus: string;
  dateRange: string;
  deliveryDate: string;
  cost: number;
  features: Feature[];
}

type PaymentStatus = 'paid' | 'pending' | 'upcoming';

interface Payment {
  id: string;
  sprint: string;
  trigger: string;
  amount: number;
  expectedDate: string;
  status: PaymentStatus;
  paidDate?: string;
}

const PAYMENTS: Payment[] = [
  { id: 'P1', sprint: 'Sprint 0: Mobilization', trigger: 'Contract signed, environment setup', amount: 4000, expectedDate: 'Tues, Feb 24', status: 'paid', paidDate: 'Feb 24, 2026' },
  { id: 'P2', sprint: 'Sprint 1: Foundation', trigger: 'Auth, onboarding, data pipeline', amount: 4000, expectedDate: 'Fri, Mar 6', status: 'paid', paidDate: 'Mar 7, 2026' },
  { id: 'P3', sprint: 'Sprint 2: The Scout', trigger: 'Scout agent + lead dashboard', amount: 4000, expectedDate: 'Fri, Mar 13', status: 'paid', paidDate: 'Mar 14, 2026' },
  { id: 'P4', sprint: 'Sprint 3: The Scribe', trigger: 'Scribe agent + Chrome Extension', amount: 4000, expectedDate: 'Fri, Mar 20', status: 'paid', paidDate: 'Mar 22, 2026' },
  { id: 'P5', sprint: 'Sprint 4: Launch', trigger: 'MVP deployed to production', amount: 4000, expectedDate: 'Fri, Mar 27', status: 'paid', paidDate: 'Mar 28, 2026' },
];

function calculatePaymentProgress() {
  const paid = PAYMENTS.filter(p => p.status === 'paid').reduce((sum, p) => sum + p.amount, 0);
  const total = PAYMENTS.reduce((sum, p) => sum + p.amount, 0);
  return { paid, total, percentage: Math.round((paid / total) * 100) };
}

function calculateTimeProgress() {
  const startDate = new Date('2026-02-24');
  const endDate = new Date('2026-03-27');
  const today = new Date();

  const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  const elapsedDays = Math.ceil((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  const remainingDays = Math.max(0, totalDays - elapsedDays);

  // Clamp percentage between 0 and 100
  const percentage = Math.min(100, Math.max(0, Math.round((elapsedDays / totalDays) * 100)));

  return {
    totalDays,
    elapsedDays: Math.max(0, elapsedDays),
    remainingDays,
    percentage
  };
}

const SPRINTS: Sprint[] = [
  {
    id: 'sprint-0',
    name: 'Sprint 0: Mobilization',
    focus: 'Infrastructure, Security, Data Design, Chrome Extension Scaffold',
    dateRange: 'Feb 24-27',
    deliveryDate: 'Fri, Feb 27',
    cost: 4000,
    features: [
      { id: 'S0-1', name: 'Environment Setup', description: 'GitHub repos with CI/CD to Vercel', status: 'completed' },
      { id: 'S0-2', name: 'Database Schema', description: 'PostgreSQL tables: users, profiles, leads, email_drafts', status: 'completed' },
      { id: 'S0-3', name: 'Security Policies', description: 'Row Level Security (RLS) for data authorization', status: 'completed' },
      { id: 'S0-4', name: 'Brand Configuration', description: 'Logo, Colors, Typography implementation', status: 'completed' },
      { id: 'S0-5', name: 'Functional Layouts', description: 'Dashboard, Settings, Admin page structures', status: 'completed' },
      { id: 'S0-6', name: 'AI Agent Framework', description: 'Base orchestration layer, Claude API connection', status: 'completed' },
      { id: 'S0-7', name: 'Chrome Extension Scaffold', description: 'Manifest V3 setup, InboxSDK, Gmail sidebar', status: 'completed' },
    ],
  },
  {
    id: 'sprint-1',
    name: 'Sprint 1: Foundation and Identity',
    focus: 'Who the user is, how they enter the system, where leads come from',
    dateRange: 'Mar 2-6',
    deliveryDate: 'Fri, Mar 6',
    cost: 4000,
    features: [
      { id: 'S1-1', name: 'Sign Up / Login', description: 'Email/password via Firebase + Google OAuth', status: 'completed' },
      { id: 'S1-2', name: 'LinkedIn OAuth', description: 'Professional network social login', status: 'pending' },
      { id: 'S1-3', name: 'The Velvet Rope', description: 'Middleware restricting premium features', status: 'completed' },
      { id: 'S1-4', name: 'Password Management', description: 'Forgot password flow + email verification', status: 'completed' },
      { id: 'S1-5', name: 'Intake Wizard', description: 'Multi-step onboarding form (5 steps)', status: 'completed' },
      { id: 'S1-6', name: 'Founder Profile', description: 'Full profile: name, website, LinkedIn, phone, title', status: 'completed' },
      { id: 'S1-7', name: 'Venture Profile', description: 'Company name, industry, stage', status: 'completed' },
      { id: 'S1-8', name: 'ICP Definition', description: 'Target industry, company size, role, geography', status: 'completed' },
      { id: 'S1-9', name: 'Outreach Preferences', description: 'Tone of voice, email signature', status: 'completed' },
      { id: 'S1-10', name: 'Niche Selection', description: 'Target vertical selection', status: 'completed' },
      { id: 'S1-11', name: 'Lead Source Integration', description: 'Connection to Apollo.io', status: 'completed' },
      { id: 'S1-12', name: 'Signal Detection', description: 'Funding, hiring, tech adoption triggers', status: 'completed' },
      { id: 'S1-13', name: 'Lead Scoring Model', description: 'ICP match, signal strength scoring', status: 'completed' },
      { id: 'S1-14', name: 'CSV Contact Upload', description: 'Upload existing contacts', status: 'completed' },
    ],
  },
  {
    id: 'sprint-2',
    name: 'Sprint 2: The Scout',
    focus: 'Automated lead generation (the overnight hunt)',
    dateRange: 'Mar 9-13',
    deliveryDate: 'Fri, Mar 13',
    cost: 4000,
    features: [
      { id: 'S2-1', name: 'ICP-Based Sourcing', description: 'Agent queries Apollo.io for matches', status: 'completed' },
      { id: 'S2-2', name: 'Signal Scanning', description: 'Agent monitors spending triggers', status: 'completed' },
      { id: 'S2-3', name: 'Daily Lead Delivery', description: '25 leads per request, email reveal working', status: 'completed' },
      { id: 'S2-4', name: 'Lead Deduplication', description: 'Prevents duplicate leads', status: 'completed' },
      { id: 'S2-5', name: 'Do Not Contact List', description: 'User-managed exclusion list', status: 'completed' },
      { id: 'S2-6', name: 'LinkedIn Data Mining', description: 'Using Apollo enrichment endpoints', status: 'completed' },
      { id: 'S2-7', name: 'Lead Queue', description: 'Lead + email draft review interface', status: 'completed' },
      { id: 'S2-8', name: 'Lead Cards', description: 'Google search + LinkedIn buttons, starring', status: 'completed' },
      { id: 'S2-9', name: 'Lead Filters', description: 'Filter by score, industry, signal, starred', status: 'completed' },
      { id: 'S2-10', name: 'Lead History', description: 'Archive of past leads', status: 'completed' },
      { id: 'S2-11', name: 'Warm Lead Detection', description: 'Flag mutual LinkedIn connections', status: 'completed' },
    ],
  },
  {
    id: 'sprint-3',
    name: 'Sprint 3: The Scribe + Chrome Extension',
    focus: 'AI-drafted emails delivered natively inside Gmail',
    dateRange: 'Mar 16-20',
    deliveryDate: 'Fri, Mar 20',
    cost: 4000,
    features: [
      { id: 'S3-1', name: 'Profile Research', description: 'Apollo enrichment with signals detection', status: 'completed' },
      { id: 'S3-2', name: 'Email Drafting', description: 'AI generates personalized email', status: 'completed' },
      { id: 'S3-3', name: 'Warm vs. Cold Tone', description: 'Different templates by lead type', status: 'completed' },
      { id: 'S3-4', name: 'Style Learning', description: 'Saves tone preferences from edits', status: 'completed' },
      { id: 'S3-5', name: 'Gmail Sidebar', description: 'Connected to real leads API', status: 'completed' },
      { id: 'S3-6', name: 'Lead + Email Review', description: 'Full detail view with draft preview', status: 'completed' },
      { id: 'S3-7', name: 'One-Click Compose', description: 'Gmail compose URL pre-filled', status: 'completed' },
      { id: 'S3-8', name: 'Edit and Send', description: 'Opens Gmail compose for editing', status: 'completed' },
      { id: 'S3-9', name: 'Skip / Reject', description: 'Status updates via API', status: 'completed' },
      { id: 'S3-10', name: 'Send Tracking', description: 'track-send + send_logs; Leads “Mark as Sent” calls trackSend (webapp)', status: 'completed' },
      { id: 'S3-11', name: 'Extension Auth', description: '/extension-auth page + token exchange', status: 'completed' },
      { id: 'S3-12', name: 'Reply Detection', description: 'classify-reply edge exists; no Gmail auto-ingest in app UI yet', status: 'pending' },
      { id: 'S3-13', name: 'Intent Classification', description: 'Claude Haiku classifies reply intent', status: 'completed' },
      { id: 'S3-14', name: 'Priority Alerts', description: 'Notifications table + warm reply alerts', status: 'completed' },
      { id: 'S3-15', name: 'Response Drafting', description: 'draft-followup edge function', status: 'completed' },
    ],
  },
  {
    id: 'sprint-4',
    name: 'Sprint 4: Launch Readiness',
    focus: 'Billing, admin, polish, and production deployment',
    dateRange: 'Mar 23-27',
    deliveryDate: 'Fri, Mar 27',
    cost: 4000,
    features: [
      { id: 'S4-1', name: 'Checkout Flow', description: 'Stripe Checkout integration', status: 'completed' },
      { id: 'S4-2', name: 'Webhook Handling', description: 'Subscription lifecycle events', status: 'completed' },
      { id: 'S4-3', name: 'Customer Portal', description: 'Self-serve billing management', status: 'completed' },
      { id: 'S4-4', name: 'Tier Gating', description: 'Feature access by subscription', status: 'completed' },
      { id: 'S4-5', name: 'Analytics Overview', description: 'Users, leads, emails, conversions', status: 'completed' },
      { id: 'S4-6', name: 'User Management', description: 'Admin table with status, activity', status: 'completed' },
      { id: 'S4-7', name: 'User Actions', description: 'View, edit role, suspend, delete', status: 'completed' },
      { id: 'S4-8', name: 'Agent Monitoring', description: 'Logs of all agent actions', status: 'completed' },
      { id: 'S4-9', name: 'Lifecycle Emails', description: 'Welcome, Verification, Receipt', status: 'completed' },
      { id: 'S4-10', name: 'Daily Briefing', description: 'send-daily-briefing + template (verify email resolution for Firebase users)', status: 'pending' },
      { id: 'S4-11', name: 'Alert Emails', description: 'Warm reply notifications', status: 'completed' },
      { id: 'S4-12', name: 'Static Pages', description: 'Terms, Privacy, FAQ, Contact', status: 'completed' },
      { id: 'S4-13', name: 'Production Deploy', description: 'Vercel production environment', status: 'completed' },
      { id: 'S4-14', name: 'Custom Domain', description: 'www.tractionbee.com configured', status: 'completed' },
      { id: 'S4-15', name: 'SSL Certificate', description: 'Automatic via Vercel', status: 'completed' },
      { id: 'S4-16', name: 'Beta Invite System', description: 'Invite codes with admin management', status: 'completed' },
      { id: 'S4-17', name: 'Extension Packaging', description: 'Chrome Web Store ready', status: 'completed' },
      { id: 'S4-18', name: 'Free Trial Config', description: 'Trial with conversion', status: 'completed' },
    ],
  },
];

function getStatusIcon(status: FeatureStatus) {
  switch (status) {
    case 'completed':
      return <Check className="h-4 w-4 text-green-400" />;
    case 'in_progress':
      return <Clock className="h-4 w-4 text-cacao-gold" />;
    case 'pending':
      return <Circle className="h-4 w-4 text-cacao-dark-tertiary" />;
  }
}

function getStatusLabel(status: FeatureStatus) {
  switch (status) {
    case 'completed':
      return 'Completed';
    case 'in_progress':
      return 'In Progress';
    case 'pending':
      return 'Pending';
  }
}

function calculateProgress(features: Feature[]) {
  const completed = features.filter((f) => f.status === 'completed').length;
  return Math.round((completed / features.length) * 100);
}

function calculateTotalProgress() {
  const allFeatures = SPRINTS.flatMap((s) => s.features);
  const completed = allFeatures.filter((f) => f.status === 'completed').length;
  return {
    completed,
    total: allFeatures.length,
    percentage: Math.round((completed / allFeatures.length) * 100),
  };
}

export function StatusPage() {
  const [authenticated, setAuthenticated] = useState(() => {
    return sessionStorage.getItem('status_auth') === 'true';
  });
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [expandedSprints, setExpandedSprints] = useState<Set<string>>(new Set(['sprint-4']));

  function toggleSprint(sprintId: string) {
    setExpandedSprints(prev => {
      const next = new Set(prev);
      if (next.has(sprintId)) {
        next.delete(sprintId);
      } else {
        next.add(sprintId);
      }
      return next;
    });
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password === STATUS_PASSWORD) {
      sessionStorage.setItem('status_auth', 'true');
      setAuthenticated(true);
      setError('');
    } else {
      setError('Incorrect password');
    }
  }

  if (!authenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cacao-dark">
        <div className="w-full max-w-sm">
          <div className="rounded-lg border border-cacao-dark-tertiary bg-cacao-dark-secondary p-6">
            <div className="mb-5 flex justify-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-cacao-dark-tertiary">
                <Lock className="h-5 w-5 text-cacao-gold" />
              </div>
            </div>
            <h1 className="mb-1.5 text-center font-heading text-lg font-medium text-[#F5F5F5]">
              Project Status
            </h1>
            <p className="mb-5 text-center text-xs text-cacao-muted">
              Enter the password to view project progress
            </p>
            <form onSubmit={handleSubmit}>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="mb-3 w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-2 text-center text-sm text-[#F5F5F5] focus:border-cacao-gold focus:outline-none focus:ring-1 focus:ring-cacao-gold"
              />
              {error && (
                <p className="mb-3 text-center text-xs text-red-400">{error}</p>
              )}
              <button
                type="submit"
                className="w-full rounded-md bg-cacao-gold px-3 py-2 text-sm font-medium text-cacao-dark hover:bg-cacao-gold-hover transition-colors"
              >
                View Status
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  const totalProgress = calculateTotalProgress();
  const paymentProgress = calculatePaymentProgress();
  const timeProgress = calculateTimeProgress();

  return (
    <div className="min-h-screen bg-cacao-dark py-10">
      <div className="mx-auto max-w-4xl px-4">
        <div className="mb-6">
          <h1 className="font-heading text-xl font-semibold text-[#F5F5F5]">Traction Bee MVP Status</h1>
          <p className="mt-1 text-sm text-cacao-muted">
            Sprint-based progress tracker for the MVP development
          </p>
        </div>

        {/* Project Info Card */}
        <div className="mb-6 rounded-lg border border-cacao-dark-tertiary bg-cacao-dark-secondary p-5">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-md bg-blue-500/20">
                <Calendar className="h-4 w-4 text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-cacao-muted">Project Start</p>
                <p className="text-sm font-medium text-[#F5F5F5]">{PROJECT_INFO.effectiveDate}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-md bg-green-500/20">
                <Flag className="h-4 w-4 text-green-400" />
              </div>
              <div>
                <p className="text-xs text-cacao-muted">Target Launch</p>
                <p className="text-sm font-medium text-[#F5F5F5]">{PROJECT_INFO.targetLaunch}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-md bg-purple-500/20">
                <DollarSign className="h-4 w-4 text-purple-400" />
              </div>
              <div>
                <p className="text-xs text-cacao-muted">Total Budget</p>
                <p className="text-sm font-medium text-[#F5F5F5]">${PROJECT_INFO.totalBudget.toLocaleString()}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-md bg-cacao-gold/20">
                <Clock className="h-4 w-4 text-cacao-gold" />
              </div>
              <div>
                <p className="text-xs text-cacao-muted">Sprint Cost</p>
                <p className="text-sm font-medium text-[#F5F5F5]">${PROJECT_INFO.sprintCost.toLocaleString()} x {PROJECT_INFO.totalSprints}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Cards - 3 columns */}
        <div className="mb-6 grid gap-4 md:grid-cols-3">
          {/* Time Elapsed */}
          <div className="rounded-lg border border-cacao-dark-tertiary bg-cacao-dark-secondary p-4">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-medium text-[#F5F5F5]">Time Elapsed</h2>
                <p className="text-xs text-cacao-muted">
                  {timeProgress.elapsedDays} of {timeProgress.totalDays} days
                </p>
              </div>
              <span className="text-lg font-semibold text-blue-400">
                {timeProgress.percentage}%
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-cacao-dark-tertiary">
              <div
                className="h-full rounded-full bg-blue-500 transition-all duration-500"
                style={{ width: `${timeProgress.percentage}%` }}
              />
            </div>
            <p className="mt-1.5 text-xs text-cacao-text-tertiary">
              {timeProgress.remainingDays} days remaining
            </p>
          </div>

          {/* Feature Progress */}
          <div className="rounded-lg border border-cacao-dark-tertiary bg-cacao-dark-secondary p-4">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-medium text-[#F5F5F5]">Features Done</h2>
                <p className="text-xs text-cacao-muted">
                  {totalProgress.completed} of {totalProgress.total} features
                </p>
              </div>
              <span className="text-lg font-semibold text-[#F5F5F5]">
                {totalProgress.percentage}%
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-cacao-dark-tertiary">
              <div
                className="h-full rounded-full bg-cacao-gold transition-all duration-500"
                style={{ width: `${totalProgress.percentage}%` }}
              />
            </div>
            <p className="mt-1.5 text-xs text-cacao-text-tertiary">
              {totalProgress.total - totalProgress.completed} features remaining
            </p>
          </div>

          {/* Payment Status */}
          <div className="rounded-lg border border-cacao-dark-tertiary bg-cacao-dark-secondary p-4">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-medium text-[#F5F5F5]">Payments</h2>
                <p className="text-xs text-cacao-muted">
                  ${paymentProgress.paid.toLocaleString()} of ${paymentProgress.total.toLocaleString()}
                </p>
              </div>
              <span className="text-lg font-semibold text-green-400">
                {paymentProgress.percentage}%
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-cacao-dark-tertiary">
              <div
                className="h-full rounded-full bg-green-500 transition-all duration-500"
                style={{ width: `${paymentProgress.percentage}%` }}
              />
            </div>
            <p className="mt-1.5 text-xs text-cacao-text-tertiary">
              ${(paymentProgress.total - paymentProgress.paid).toLocaleString()} remaining
            </p>
          </div>
        </div>

        <div className="mb-6 rounded-lg border border-cacao-dark-tertiary bg-cacao-dark-secondary">
          <div className="border-b border-cacao-dark-tertiary p-4">
            <h3 className="flex items-center gap-2 text-sm font-medium text-[#F5F5F5]">
              <DollarSign className="h-4 w-4" />
              Payment Schedule
            </h3>
            <p className="mt-0.5 text-xs text-cacao-muted">Due within 3 business days of sprint completion</p>
          </div>
          <div className="divide-y divide-cacao-dark-tertiary">
            {PAYMENTS.map((payment) => (
              <div key={payment.id} className="flex items-center justify-between px-5 py-3">
                <div className="flex items-center gap-3">
                  <div className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full ${
                    payment.status === 'paid'
                      ? 'bg-green-500/20'
                      : payment.status === 'pending'
                      ? 'bg-cacao-gold/20'
                      : 'bg-cacao-dark-tertiary'
                  }`}>
                    {payment.status === 'paid' ? (
                      <Check className="h-3.5 w-3.5 text-green-400" />
                    ) : payment.status === 'pending' ? (
                      <Clock className="h-3.5 w-3.5 text-cacao-gold" />
                    ) : (
                      <Circle className="h-3.5 w-3.5 text-cacao-muted" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#F5F5F5]">{payment.sprint}</p>
                    <p className="text-xs text-cacao-muted">{payment.trigger}</p>
                    <p className="text-xs text-cacao-text-tertiary">
                      {payment.status === 'paid'
                        ? `Paid ${payment.paidDate}`
                        : `Expected ${payment.expectedDate}`}
                    </p>
                  </div>
                </div>
                <div className="flex-shrink-0 text-right">
                  <p className="text-sm font-medium text-[#F5F5F5]">${payment.amount.toLocaleString()}</p>
                  <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                    payment.status === 'paid'
                      ? 'bg-green-500/20 text-green-400'
                      : payment.status === 'pending'
                      ? 'bg-cacao-gold/20 text-cacao-gold'
                      : 'bg-cacao-dark-tertiary text-cacao-muted'
                  }`}>
                    {payment.status === 'paid' ? 'Paid' : payment.status === 'pending' ? 'Due Now' : 'Upcoming'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          {SPRINTS.map((sprint, index) => {
            const progress = calculateProgress(sprint.features);
            const completedCount = sprint.features.filter(
              (f) => f.status === 'completed'
            ).length;
            const isExpanded = expandedSprints.has(sprint.id);
            const payment = PAYMENTS[index];

            return (
              <div
                key={sprint.id}
                className="rounded-lg border border-cacao-dark-tertiary bg-cacao-dark-secondary overflow-hidden"
              >
                {/* Clickable Header */}
                <button
                  onClick={() => toggleSprint(sprint.id)}
                  className="w-full p-5 text-left hover:bg-cacao-dark-tertiary/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-medium text-[#F5F5F5]">
                          {sprint.name}
                        </h3>
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4 text-cacao-muted" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-cacao-muted" />
                        )}
                      </div>
                      <p className="mt-0.5 text-xs text-cacao-muted">{sprint.focus}</p>

                      {/* Sprint metadata badges */}
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center gap-1 text-xs text-cacao-muted">
                          <Calendar className="h-3 w-3" />
                          {sprint.dateRange}
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs text-blue-400">
                          <Flag className="h-3 w-3" />
                          Due: {sprint.deliveryDate}
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-400">
                          <DollarSign className="h-3 w-3" />
                          ${sprint.cost.toLocaleString()}
                          {payment?.status === 'paid' && (
                            <span className="ml-1 rounded bg-green-500/20 px-1.5 py-0.5 text-xs font-medium text-green-400">
                              PAID
                            </span>
                          )}
                          {payment?.status === 'pending' && (
                            <span className="ml-1 rounded bg-cacao-gold/20 px-1.5 py-0.5 text-xs font-medium text-cacao-gold">
                              DUE
                            </span>
                          )}
                        </span>
                      </div>
                    </div>

                    {/* Progress indicator */}
                    <div className="flex-shrink-0 text-right">
                      <span className="text-lg font-semibold text-[#F5F5F5]">
                        {progress}%
                      </span>
                      <p className="text-xs text-cacao-muted">
                        {completedCount}/{sprint.features.length} done
                      </p>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-cacao-dark-tertiary">
                    <div
                      className="h-full rounded-full bg-cacao-gold transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </button>

                {/* Expandable Features List */}
                {isExpanded && (
                  <div className="border-t border-cacao-dark-tertiary divide-y divide-cacao-dark-tertiary/50">
                    {sprint.features.map((feature) => (
                      <div
                        key={feature.id}
                        className="flex items-center gap-3 px-5 py-2.5"
                      >
                        <div className="flex-shrink-0">
                          {getStatusIcon(feature.status)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium text-[#F5F5F5]">
                            <span className="text-cacao-muted">{feature.id}</span>{' '}
                            {feature.name}
                          </p>
                          <p className="truncate text-xs text-cacao-text-tertiary">
                            {feature.description}
                          </p>
                        </div>
                        <span
                          className={`flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
                            feature.status === 'completed'
                              ? 'bg-green-500/20 text-green-400'
                              : feature.status === 'in_progress'
                              ? 'bg-cacao-gold/20 text-cacao-gold'
                              : 'bg-cacao-dark-tertiary text-cacao-muted'
                          }`}
                        >
                          {getStatusLabel(feature.status)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-6 space-y-2 text-center text-xs text-cacao-text-tertiary">
          <p>
            Last updated: April 5, 2026 ({totalProgress.completed}/{totalProgress.total} features marked complete –{' '}
            {totalProgress.percentage}%).
          </p>
          <p className="max-w-xl mx-auto text-cacao-muted">
            Known gaps: LinkedIn OAuth login (S1-2); reply automation from Gmail not wired in UI (S3-12); daily briefing
            cron needs a reliable email source for Firebase-backed accounts (S4-10). Send logging from web: Mark as Sent →{' '}
            <code className="text-cacao-text-tertiary">trackSend</code> / <code className="text-cacao-text-tertiary">track-send</code>.
          </p>
          <p>Target launch: Friday, March 27, 2026 (per project card)</p>
        </div>
      </div>
    </div>
  );
}
